import type { ReactNode } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import {
  getOnOffLabel,
  parseBooleanLike,
} from '@/features/equipment-browser/utils/boolean-display';
import { Badge } from '@/ux-common/components/badge/badge';
import { cn } from '@/ux-common/lib/utils';

interface EquipmentBulkEditTableSwitchCellProps {
  columnId: string;
  disabled?: boolean;
  rowId: string;
}

/**
 * Cell that displays a clickable badge for boolean/switch-type settings.
 * Clicking toggles the value between 'true' and 'false'.
 *
 * Uses displayUnits to determine the badge label via formatUnits
 * (e.g., "ON"/"OFF" for on-off, "OPEN"/"CLOSED" for open-closed).
 * Badge variant is derived from the boolean value: true → good, false → tier2.
 */
export const EquipmentBulkEditTableSwitchCell = ({
  columnId,
  disabled,
  rowId,
}: EquipmentBulkEditTableSwitchCellProps): ReactNode => {
  const { control } = useFormContext();
  const fieldName = `values.${rowId}.${columnId}`;
  const { field } = useController({
    control,
    name: fieldName,
    defaultValue: '',
  });

  const currentValue = field.value as string;

  const handleClick = () => {
    if (disabled) return;

    if (!currentValue || currentValue === '') {
      field.onChange('true');
    } else if (currentValue === 'true') {
      field.onChange('false');
    } else {
      field.onChange('true');
    }
  };

  // TODO(UX-3724): This currently forces On/Off labels; switch to required boolean displayUnits
  // from display_template when that migration lands. https://crossnokaye.atlassian.net/browse/UX-3724
  const label = getOnOffLabel(currentValue, 'title');
  const parsedCurrentValue = parseBooleanLike(currentValue);

  return (
    <div className={'flex w-full'}>
      <Badge
        aria-disabled={disabled}
        className={cn(
          disabled ? 'hover:cursor-default cursor-not-allowed' : 'hover:cursor-pointer',
          'badge',
          parsedCurrentValue ? 'badge-good' : 'badge-tier2'
        )}
        onClick={handleClick}
      >
        {label}
      </Badge>
    </div>
  );
};
